<script setup lang="ts">
import { reactive, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import every from 'lodash/every';
import omit from 'lodash/omit';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import { useMixins } from './useMixins';
import { LANG_ENGLISH, LANG_FRENCH } from '@/constants';
import { usePromosStore } from '@/store/PromosStore';
import { PromosEnum } from '@/store/enums/PromosEnum';
import { usePromoComposable } from '@/components/admin/usePromoComposable';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Splitter from 'primevue/splitter';
import SplitterPanel from 'primevue/splitterpanel';
import InputTextAera from 'primevue/textarea';
import Toast from 'primevue/toast';
import TopBanner from '@/components/promos/TopBanner.vue';
import UploadMultipleImages from './UploadMultipleImages.vue';

const { t } = useI18n({ useScope: 'global' });
const { canEditPromo } = usePromoComposable();
const promosStore = usePromosStore();
const toast = useToast();
const confirm = useConfirm();
const isShowError = ref(false);
const previewData = ref(null);
const multipleUploadEn = ref();
const multipleUploadFr = ref();
const visiblePreview = ref(false);
const headerPreviewTopBanner = ref('');

onBeforeMount(async () => {
  await getTopBarInfo();
});

const payload: any = reactive({
  promotionType: PromosEnum.promotionType.TOP_BANNER,
  titleEn: '',
  titleFr: '',
  subTitleEn: '',
  subTitleFr: '',
  descEn: '',
  descFr: '',
  imgUrlEn: '',
  imgTitleEn: '',
  imgUrlFr: '',
  imgTitleFr: '',
  sk: '',
  imageEn: {
    desktop: '',
    mobile: '',
    tablet: '',
    widescreen: ''
  },
  imageFr: {
    desktop: '',
    mobile: '',
    tablet: '',
    widescreen: ''
  }
});
const { currentLanguage, v$, previewEn, previewFr } = useMixins(payload);
const objectCopy = ref();
const handleCancel = () => {
  for (const key in payload) {
    payload[key] = objectCopy.value?.[key];
  }
  multipleUploadEn.value.resetFileUpload();
  multipleUploadFr.value.resetFileUpload();
  getTopBarInfo();
};

const getTopBarInfo = async () => {
  await promosStore.getTopbarInfo().then((res) => {
    if (res.length > 0) {
      for (const property in payload) {
        payload[property] = res[0][property];
      }
      objectCopy.value = { ...payload };
      handlePreview(currentLanguage === LANG_ENGLISH ? previewEn.value : previewFr.value);
    }
  });
};
const handlePreview = (payloadPreview) => {
  previewData.value = { ...payloadPreview, currentLanguage };
};

const handleSubmit = async () => {
  const isEmptyPayload = every(omit(cloneDeep(payload), ['sk', 'promotionType', 'imageEn', 'imageFr']), isEmpty);
  if (isEmptyPayload) {
    toast.add({ severity: 'error', summary: '', detail: t('validate.at_least_field_required'), life: 5000 });
    return;
  }
  isShowError.value = false;
  promosStore.submitPromos(payload, 'top-banners').then(async () => {
    toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    isShowError.value = false;
    multipleUploadEn.value.resetFileUpload();
    multipleUploadFr.value.resetFileUpload();
    await getTopBarInfo();
  });
};
const handleDelete = async () => {
  confirm.require({
    message: t('promos.labels.confirm_delete'),
    header: 'Confirmation',
    icon: 'pi pi-exclamation-triangle',
    accept: () => {
      promosStore
        .deletePromos(payload.sk, PromosEnum.path.TOP_BANNER)
        .then(() => {
          toast.add({ severity: 'success', summary: '', detail: t('notification.delete_successfully'), life: 3000 });
          for (const property in payload) {
            payload[property] = '';
          }
          payload.promotionType = PromosEnum.promotionType.TOP_BANNER;
          multipleUploadEn.value.resetFileUpload();
          multipleUploadFr.value.resetFileUpload();
          previewData.value = null;
        })
        .catch((res) => {
          toast.add({ severity: 'error', summary: '', detail: res?.message, life: 5000 });
        });
    }
  });
};

const onUploadDataTopBannersEn = (dataImage) => {
  payload.imageEn = { ...dataImage };
};

const onUploadDataTopBannersFr = (dataImage) => {
  payload.imageFr = { ...dataImage };
};

const handleRemovePreviewImg = (key, lang: string) => {
  if (lang === LANG_ENGLISH) {
    payload.imageEn[key] = '';
    return;
  }
  payload.imageFr[key] = '';
};

const openDialogPreviewTopBanner = (previewData: any, language: string) => {
  handlePreview(previewData);
  visiblePreview.value = true;
  if (language === LANG_ENGLISH) {
    headerPreviewTopBanner.value = t('promos.labels.preview_english');
    return;
  }
  headerPreviewTopBanner.value = t('promos.labels.preview_french');
};
</script>

<template>
  <Toast position="top-left" group="tl" />
  <Splitter layout="vertical">
    <SplitterPanel class="mt-5 px-3" v-if="previewData">
      <TopBanner :value="previewData"></TopBanner>
    </SplitterPanel>
    <SplitterPanel class="main-splitter">
      <Splitter class="content">
        <SplitterPanel class="p-3" :size="50">
          <h5 class="mb-5">{{ $t('promos.labels.english') }}</h5>
          <div class="flex flex-col">
            <span class="p-float-label mb-5">
              <InputText :disabled="!canEditPromo" id="topbanner-titleEn" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.titleEn" />
              <label for="topbanner-titleEn">{{ $t('promos.labels.title') }}</label>
            </span>
            <span class="text-red-500" v-if="isShowError && v$.titleEn.$invalid">
              {{ v$.titleEn.required.$message }}
            </span>
            <span class="p-float-label mb-5">
              <InputText :disabled="!canEditPromo" id="topbanner-subTitleEn" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.subTitleEn" />
              <label for="topbanner-subTitleEn">{{ $t('promos.labels.subtitle') }}</label>
            </span>
            <span class="text-red-500" v-if="isShowError && v$.subTitleEn.$invalid">
              {{ v$.subTitleEn.required.$message }}
            </span>
            <span class="p-float-label mb-5">
              <InputTextAera :disabled="!canEditPromo" id="topbanner-descEn" class="mb-1 w-full" v-model.trim="payload.descEn" :autoResize="true" rows="5" cols="30" />
              <label for="topbanner-descEn">{{ $t('promos.labels.description') }}</label>
            </span>
            <span class="text-red-500" v-if="isShowError && v$.descEn.$invalid">
              {{ v$.descEn.required.$message }}
            </span>
            <span class="p-float-label mb-5">
              <InputText :disabled="!canEditPromo" id="topbanner-imgTitleEn" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.imgTitleEn" />
              <label for="topbanner-imgTitleEn">{{ $t('promos.labels.image_title') }}</label>
            </span>
            <span class="text-red-500" v-if="isShowError && v$.imgTitleEn.$invalid">
              {{ v$.imgTitleEn.required.$message }}
            </span>
            <div>
              <UploadMultipleImages ref="multipleUploadEn" @handleUploadData="onUploadDataTopBannersEn($event)" :listUrlPreview="payload.imageEn" @handleRemoveImgReview="(key) => handleRemovePreviewImg(key, LANG_ENGLISH)"></UploadMultipleImages>
            </div>
            <Button id="top-banner-preview-en-btn" :label="$t('promos.labels.preview')" icon="pi pi-eye" class="mt-3 btn-en-preview" @click="openDialogPreviewTopBanner(previewEn, LANG_ENGLISH)" />
          </div>
        </SplitterPanel>
        <SplitterPanel class="p-3" :size="50">
          <h5 class="mb-5">{{ $t('promos.labels.french') }}</h5>
          <div class="flex flex-col">
            <span class="p-float-label mb-5">
              <InputText :disabled="!canEditPromo" id="topbanner-titleFr" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.titleFr" />
              <label for="topbanner-titleFr">{{ $t('promos.labels.title') }}</label>
            </span>
            <span class="text-red-500" v-if="isShowError && v$.titleFr.$invalid">
              {{ v$.titleFr.required.$message }}
            </span>
            <span class="p-float-label mb-5">
              <InputText :disabled="!canEditPromo" id="topbanner-subTitleFr" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.subTitleFr" />
              <label for="topbanner-subTitleFr">{{ $t('promos.labels.subtitle') }}</label>
            </span>
            <span class="text-red-500" v-if="isShowError && v$.subTitleFr.$invalid">
              {{ v$.subTitleFr.required.$message }}
            </span>
            <span class="p-float-label mb-5">
              <InputTextAera :disabled="!canEditPromo" id="topbanner-descFr" class="mb-1 w-full" v-model.trim="payload.descFr" :autoResize="true" rows="5" cols="30" />
              <label for="topbanner-descFr">{{ $t('promos.labels.description') }}</label>
            </span>
            <span class="text-red-500" v-if="isShowError && v$.descFr.$invalid">
              {{ v$.descFr.required.$message }}
            </span>
            <span class="p-float-label mb-5">
              <InputText :disabled="!canEditPromo" id="topbanner-imgTitleFr" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.imgTitleFr" />
              <label for="topbanner-imgTitleFr">{{ $t('promos.labels.image_title') }}</label>
            </span>
            <span class="text-red-500" v-if="isShowError && v$.imgTitleFr.$invalid">
              {{ v$.imgTitleFr.required.$message }}
            </span>
            <div>
              <UploadMultipleImages ref="multipleUploadFr" @handleUploadData="onUploadDataTopBannersFr($event)" :listUrlPreview="payload.imageFr" @handleRemoveImgReview="(key) => handleRemovePreviewImg(key, LANG_FRENCH)"></UploadMultipleImages>
            </div>
            <Button id="top-banner-preview-fr-btn" :label="$t('promos.labels.preview')" icon="pi pi-eye" class="mt-3 btn-fr-preview" @click="openDialogPreviewTopBanner(previewFr, LANG_FRENCH)" />
          </div>
        </SplitterPanel>
      </Splitter>
    </SplitterPanel>
    <SplitterPanel class="p-3 text-center justify-content-center" v-if="canEditPromo">
      <Button id="top-banner-save-btn" :label="$t('promos.save')" icon="pi pi-check" class="p-button-secondary mr-2" @click="handleSubmit()" />
      <Button id="top-banner-delete-btn" :disabled="!payload.sk" :label="$t('promos.delete')" icon="pi pi-trash" class="mr-2" @click="handleDelete()" />
      <Button id="top-banner-cancel-btn" :label="$t('promos.labels.cancel')" icon="pi pi-times" @click="handleCancel()" />
    </SplitterPanel>
  </Splitter>
  <Dialog :header="headerPreviewTopBanner" v-model:visible="visiblePreview" :modal="true" class="w-7">
    <TopBanner :value="previewData"></TopBanner>
  </Dialog>
</template>

<style lang="scss" scoped>
.p-button.p-component {
  padding: 0.75rem 1.25rem;
}
</style>
